import React, { useEffect, useRef, useState } from 'react';
import {
  getMezzi, getMulte, createMulta, updateMulta, deleteMulta,
  exportMulteXlsx, importMulteXlsx,
  Mezzo, Multa,
} from '../../services/fleet';

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

const EMPTY: Partial<Multa> = {
  data_multa: new Date().toISOString().split('T')[0],
  costo: 0,
  pagata: false,
  notificata_autista: false,
  addebitata_autista: false,
};

export default function MulteFleet() {
  const [mezzi, setMezzi] = useState<Mezzo[]>([]);
  const [multe, setMulte] = useState<Multa[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterMezzo, setFilterMezzo] = useState<number | ''>('');
  const [filterPagata, setFilterPagata] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Multa | null>(null);
  const [form, setForm] = useState<Partial<Multa>>(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [importing, setImporting] = useState(false);
  const importRef = useRef<HTMLInputElement>(null);

  const handleExport = async () => {
    try {
      const params: Record<string, unknown> = {};
      if (filterMezzo) params.mezzo_id = filterMezzo;
      if (filterPagata !== '') params.pagata = filterPagata === 'true';
      const res = await exportMulteXlsx(params);
      downloadBlob(res.data as Blob, 'fleet_multe.xlsx');
    } catch { setError('Errore export'); }
  };

  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const res = await importMulteXlsx(file);
      const { created, updated, errors } = res.data;
      alert(`Import completato: ${created} create, ${updated} aggiornate${errors.length ? '\nErrori: ' + errors.join('\n') : ''}`);
      load();
    } catch { setError('Errore import'); }
    finally { setImporting(false); if (importRef.current) importRef.current.value = ''; }
  };

  useEffect(() => {
    getMezzi(false).then(r => setMezzi(r.data));
    load();
  }, []);

  useEffect(() => { load(); }, [filterMezzo, filterPagata]);

  const load = async () => {
    setLoading(true);
    try {
      const params: Record<string, unknown> = {};
      if (filterMezzo)     params.mezzo_id = filterMezzo;
      if (filterPagata !== '') params.pagata = filterPagata === 'true';
      const res = await getMulte(params);
      setMulte(res.data);
    } catch {
      setError('Errore caricamento');
    } finally {
      setLoading(false);
    }
  };

  const openNew = () => {
    setEditing(null);
    setForm({ ...EMPTY, mezzo_id: filterMezzo || undefined });
    setShowForm(true);
    setError('');
  };

  const openEdit = (m: Multa) => {
    setEditing(m);
    setForm({ ...m });
    setShowForm(true);
    setError('');
  };

  const handleSave = async () => {
    if (!form.mezzo_id)                { setError('Seleziona un veicolo'); return; }
    if (!form.costo || form.costo <= 0) { setError('Costo obbligatorio'); return; }
    setSaving(true);
    setError('');
    try {
      if (editing) {
        await updateMulta(editing.id, form);
      } else {
        await createMulta(form);
      }
      setShowForm(false);
      load();
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Errore salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Eliminare questa multa?')) return;
    try { await deleteMulta(id); load(); } catch { setError('Errore eliminazione'); }
  };

  const togglePagata = async (m: Multa) => {
    try {
      await updateMulta(m.id, {
        pagata: !m.pagata,
        data_pagamento: !m.pagata ? new Date().toISOString().split('T')[0] : undefined,
      });
      load();
    } catch { setError('Errore aggiornamento'); }
  };

  const set = (patch: Partial<Multa>) => setForm(f => ({ ...f, ...patch }));

  const totAperte = multe.filter(m => !m.pagata).reduce((s, m) => s + m.costo, 0);
  const totPagate = multe.filter(m =>  m.pagata).reduce((s, m) => s + m.costo, 0);

  return (
    <div className="fleet-section">
      <div className="fleet-toolbar">
        <select className="fleet-filter" value={filterMezzo} onChange={e => setFilterMezzo(parseInt(e.target.value) || '')}>
          <option value="">Tutti i veicoli</option>
          {mezzi.map(m => <option key={m.id} value={m.id}>{m.targa} — {m.marca} {m.modello}</option>)}
        </select>
        <select className="fleet-filter" value={filterPagata} onChange={e => setFilterPagata(e.target.value)}>
          <option value="">Tutte</option>
          <option value="false">Non pagate</option>
          <option value="true">Pagate</option>
        </select>
        <button className="btn-secondary" onClick={handleExport} title="Scarica XLSX">⬇ XLSX</button>
        <button className="btn-secondary" onClick={() => importRef.current?.click()} disabled={importing} title="Carica XLSX">
          {importing ? '...' : '⬆ XLSX'}
        </button>
        <input ref={importRef} type="file" accept=".xlsx" style={{ display: 'none' }} onChange={handleImport} />
        <button className="btn-primary" onClick={openNew}>+ Nuova Multa</button>
      </div>

      <div style={{ display: 'flex', gap: 16, marginBottom: 14 }}>
        <div style={{ background: '#fee2e2', border: '1px solid #fecaca', borderRadius: 8, padding: '8px 16px', minWidth: 140 }}>
          <div style={{ fontSize: '0.72rem', color: '#991b1b', fontWeight: 700, textTransform: 'uppercase' }}>Da pagare</div>
          <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#7f1d1d' }}>€ {totAperte.toFixed(2)}</div>
        </div>
        <div style={{ background: '#dcfce7', border: '1px solid #bbf7d0', borderRadius: 8, padding: '8px 16px', minWidth: 140 }}>
          <div style={{ fontSize: '0.72rem', color: '#15803d', fontWeight: 700, textTransform: 'uppercase' }}>Pagate</div>
          <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#14532d' }}>€ {totPagate.toFixed(2)}</div>
        </div>
      </div>

      {error && <div className="fleet-error">{error}</div>}

      {loading ? (
        <div className="fleet-loading">Caricamento...</div>
      ) : (
        <table className="fleet-table">
          <thead>
            <tr>
              <th>Veicolo</th>
              <th>Autista</th>
              <th>N° Verbale</th>
              <th>Località</th>
              <th>Data multa</th>
              <th>Costo</th>
              <th>Pagata</th>
              <th>Notif. Autista</th>
              <th>Addebitata</th>
              <th>Azioni</th>
            </tr>
          </thead>
          <tbody>
            {multe.length === 0 ? (
              <tr><td colSpan={10} style={{ textAlign: 'center', padding: 30, color: '#9ca3af' }}>Nessuna multa trovata</td></tr>
            ) : multe.map(m => (
              <tr key={m.id}>
                <td><strong>{m.mezzo_targa}</strong></td>
                <td>{m.autista_nome || '—'}</td>
                <td>{m.n_verbale || '—'}</td>
                <td>{m.localita || '—'}</td>
                <td>{new Date(m.data_multa).toLocaleDateString('it-IT')}</td>
                <td><strong>€ {m.costo.toFixed(2)}</strong></td>
                <td>
                  <button
                    onClick={() => togglePagata(m)}
                    style={{
                      background: m.pagata ? '#dcfce7' : '#fee2e2',
                      color: m.pagata ? '#15803d' : '#991b1b',
                      border: 'none', borderRadius: 6,
                      padding: '3px 10px', cursor: 'pointer',
                      fontSize: '0.8rem', fontWeight: 600,
                    }}
                  >
                    {m.pagata ? '✅ Sì' : '❌ No'}
                  </button>
                  {m.data_pagamento && (
                    <div style={{ fontSize: '0.7rem', color: '#6b7280', marginTop: 2 }}>
                      {new Date(m.data_pagamento).toLocaleDateString('it-IT')}
                    </div>
                  )}
                </td>
                <td>
                  <span style={{
                    background: m.notificata_autista ? '#dcfce7' : '#f3f4f6',
                    color: m.notificata_autista ? '#15803d' : '#6b7280',
                    padding: '2px 8px', borderRadius: 6, fontSize: '0.8rem',
                  }}>
                    {m.notificata_autista ? 'Sì' : 'No'}
                  </span>
                </td>
                <td>
                  <span style={{
                    background: m.addebitata_autista ? '#fef9c3' : '#f3f4f6',
                    color: m.addebitata_autista ? '#854d0e' : '#6b7280',
                    padding: '2px 8px', borderRadius: 6, fontSize: '0.8rem',
                  }}>
                    {m.addebitata_autista ? 'Sì' : 'No'}
                  </span>
                </td>
                <td>
                  <button className="btn-icon" onClick={() => openEdit(m)}>✏️</button>
                  <button className="btn-icon btn-danger" onClick={() => handleDelete(m.id)}>🗑️</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showForm && (
        <div className="fleet-modal-overlay" onClick={() => setShowForm(false)}>
          <div className="fleet-modal" onClick={e => e.stopPropagation()}>
            <div className="fleet-modal-header">
              <h2>{editing ? 'Modifica Multa' : 'Nuova Multa'}</h2>
              <button onClick={() => setShowForm(false)}>✕</button>
            </div>

            <div className="fleet-modal-body">
              {error && <div className="fleet-error">{error}</div>}

              <div className="form-section-title">Dati verbale</div>
              <div className="form-grid-3">
                <div className="form-group" style={{ gridColumn: 'span 2' }}>
                  <label>Veicolo *</label>
                  <select value={form.mezzo_id || ''} onChange={e => set({ mezzo_id: parseInt(e.target.value) || undefined })}>
                    <option value="">— Seleziona —</option>
                    {mezzi.map(m => <option key={m.id} value={m.id}>{m.targa} — {m.marca} {m.modello}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Costo (€) *</label>
                  <input type="number" step="0.01" value={form.costo || ''} onChange={e => set({ costo: parseFloat(e.target.value) || 0 })} />
                </div>
                <div className="form-group">
                  <label>N° Verbale</label>
                  <input value={form.n_verbale || ''} onChange={e => set({ n_verbale: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Data multa *</label>
                  <input type="date" value={form.data_multa || ''} onChange={e => set({ data_multa: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Data notifica</label>
                  <input type="date" value={form.data_notifica || ''} onChange={e => set({ data_notifica: e.target.value })} />
                </div>
                <div className="form-group" style={{ gridColumn: 'span 3' }}>
                  <label>Località</label>
                  <input value={form.localita || ''} onChange={e => set({ localita: e.target.value })} placeholder="Via, Città..." />
                </div>
              </div>

              <div className="form-section-title">Pagamento & Addebiti</div>
              <div className="form-grid-3">
                <div className="form-group">
                  <label>Pagata</label>
                  <select value={form.pagata ? 'true' : 'false'} onChange={e => set({ pagata: e.target.value === 'true' })}>
                    <option value="false">No</option>
                    <option value="true">Sì</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Data pagamento</label>
                  <input type="date" value={form.data_pagamento || ''} onChange={e => set({ data_pagamento: e.target.value })} />
                </div>
                <div className="form-group" />
                <div className="form-group">
                  <label>Notificata autista</label>
                  <select value={form.notificata_autista ? 'true' : 'false'} onChange={e => set({ notificata_autista: e.target.value === 'true' })}>
                    <option value="false">No</option>
                    <option value="true">Sì</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Data notifica autista</label>
                  <input type="date" value={form.data_notifica_autista || ''} onChange={e => set({ data_notifica_autista: e.target.value })} />
                </div>
                <div className="form-group" />
                <div className="form-group">
                  <label>Addebitata autista</label>
                  <select value={form.addebitata_autista ? 'true' : 'false'} onChange={e => set({ addebitata_autista: e.target.value === 'true' })}>
                    <option value="false">No</option>
                    <option value="true">Sì</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Data addebito</label>
                  <input type="date" value={form.data_addebito || ''} onChange={e => set({ data_addebito: e.target.value })} />
                </div>
              </div>

              <div className="form-section-title">Note</div>
              <div className="form-group">
                <textarea rows={2} value={form.note || ''} onChange={e => set({ note: e.target.value })} />
              </div>
            </div>

            <div className="fleet-modal-footer">
              <button className="btn-secondary" onClick={() => setShowForm(false)}>Annulla</button>
              <button className="btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Salvataggio...' : (editing ? 'Aggiorna' : 'Salva')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
